// Pure imgproxy URL construction — no crypto, no network, no server round-trip.
//
// imgproxy runs with signature verification disabled (it skips verification
// only when BOTH IMGPROXY_KEY and IMGPROXY_SALT are unset), so a URL is just a
// deterministic function of (source path, processing options). That means the
// browser and the SSR renderer produce byte-identical URLs, and no /api/*
// endpoint has to hand signed URLs back to the client.
//
// What replaces the signature as the abuse gate is the processing-option
// allowlist in the media cache nginx config — see docker/nginx-media-cache.conf
// and the matching ConfigMaps in both k8s repos. Anything this module can emit
// MUST match that allowlist, or nginx will 403 it. The constants below are the
// contract between the two; changing one without the other breaks images.

/**
 * The leading path segment, where imgproxy expects a signature.
 *
 * imgproxy does not verify it while signing is off, and treats ANY value as an
 * (ignored) signature — the conventional placeholder is "insecure", but that
 * word ends up in every public image URL and in the image entries Google
 * crawls from our sitemap, where it reads as a warning about the site rather
 * than a note about imgproxy's URL format. "img" is equivalent to imgproxy and
 * doesn't invite that misreading.
 *
 * The segment CANNOT simply be dropped. imgproxy would then consume
 * "rs:fill:640:480" as the signature and silently serve the un-resized
 * original — HTTP 200, wrong image, no error anywhere.
 *
 * The nginx allowlist matches any [A-Za-z0-9_-]+ here, so changing this value
 * needs no infrastructure change. It does change every URL, so decide before a
 * rollout rather than after.
 */
export const IMGPROXY_PATH_PREFIX = 'img';

/** Longest edge nginx will accept. Keep in sync with the allowlist regex. */
export const IMGPROXY_MAX_DIMENSION = 2048;

/** Output formats nginx will accept. Keep in sync with the allowlist regex. */
export const IMGPROXY_FORMATS = ['webp', 'avif', 'jpeg', 'jpg', 'png'] as const;
export type ImgproxyFormat = (typeof IMGPROXY_FORMATS)[number];

export type ImgproxyResizeMode = 'fill' | 'fit';

// Single imgproxy processing-options string used as the canonical "master"
// variant for SEO surfaces: og:image, twitter:image, JSON-LD ImageObject,
// and the image XML sitemap.
//
// Why: imgproxy emits a distinct URL per parameter combination, so without a
// canonical pick Google sees ~14 variants per source photo (different
// sizes/formats from responsive srcset) and indexes none of them. Pinning one
// variant for SEO surfaces — while leaving responsive srcsets alone for
// rendering — gives crawlers a single representative URL per source image.
export const MASTER_IMGPROXY_OPTION = 'rs:fill:1200:900/q:85/f:webp';

/**
 * base64url, no padding. Works identically under Node and in the browser:
 * TextEncoder + btoa are global in both, whereas Buffer is Node-only.
 * Going via TextEncoder (rather than btoa directly) keeps non-ASCII filenames
 * correct — btoa throws on code points above U+00FF.
 */
function toBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Normalise a Strapi media url into the source imgproxy should fetch.
 *
 * We deliberately emit a RELATIVE path ("/uploads/foo.webp") rather than an
 * absolute one. imgproxy prepends IMGPROXY_BASE_URL whenever the source does
 * not already start with it, so the origin hostname lives entirely in the
 * imgproxy deployment. Two benefits: the frontend needs no knowledge of the
 * internal cluster host, and that host stops appearing (base64-decodable) in
 * every page's HTML.
 *
 * The single LEADING SLASH is deliberate and load-bearing. imgproxy does a
 * plain string concatenation, so the result depends on how BASE_URL is written:
 *
 *   base "http://origin/"  + "/uploads/x" -> "http://origin//uploads/x"  (works;
 *                                            this is what we already serve today)
 *   base "http://origin"   + "/uploads/x" -> "http://origin/uploads/x"   (works)
 *   base "http://origin"   +  "uploads/x" -> "http://originuploads/x"    (broken)
 *
 * Keeping the slash makes us correct whether or not BASE_URL ends in one —
 * which matters because staging's BASE_URL lives in a sealed secret we cannot
 * read from the repo.
 *
 * Absolute URLs are passed through untouched for the case where Strapi is
 * switched to a remote upload provider; IMGPROXY_ALLOWED_SOURCES is what
 * decides whether such a source is actually permitted.
 */
export function normaliseImgproxySource(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `/${path.replace(/^\/+/, '')}`;
}

/**
 * Scale (width, height) down so neither exceeds IMGPROXY_MAX_DIMENSION,
 * preserving aspect ratio. Both are clamped to at least 1.
 *
 * This is not cosmetic: Strapi originals are routinely wider than 2048, and
 * Picture.vue always emits one variant at the source's native size. Without
 * clamping those URLs would be 403'd by the nginx allowlist and the largest
 * srcset candidate would silently vanish.
 */
export function clampDimensions(width: number, height: number): [number, number] {
  const w = Math.max(1, Math.round(width) || 1);
  const h = Math.max(1, Math.round(height) || 1);
  const longest = Math.max(w, h);
  if (longest <= IMGPROXY_MAX_DIMENSION) return [w, h];

  const scale = IMGPROXY_MAX_DIMENSION / longest;
  return [Math.max(1, Math.round(w * scale)), Math.max(1, Math.round(h * scale))];
}

export interface ResizeOptionInput {
  width: number;
  height: number;
  quality: number;
  format: string;
  mode?: ImgproxyResizeMode;
}

/**
 * Build a processing-options string in the exact shape the nginx allowlist
 * accepts: rs:<mode>:<w>:<h>/q:<1-100>/f:<format>
 */
export function buildResizeOption({ width, height, quality, format, mode = 'fill' }: ResizeOptionInput): string {
  const [w, h] = clampDimensions(width, height);
  const q = Math.min(100, Math.max(1, Math.round(quality) || 1));
  return `rs:${mode}:${w}:${h}/q:${q}/f:${format}`;
}

/**
 * Assemble the full public URL.
 *
 * @param path      Strapi media url, e.g. "/uploads/foo_abc123.webp"
 * @param option    processing options, e.g. from buildResizeOption()
 * @param publicUrl public media host, e.g. "https://media.herbertseidl.at"
 *
 * Returns '' when any input is missing so callers can render a placeholder
 * rather than emitting <img src="undefined">.
 */
export function buildImgproxyUrl(path: string | undefined | null, option: string, publicUrl: string | undefined | null): string {
  if (!path || !option || !publicUrl) return '';
  const host = publicUrl.replace(/\/+$/, '');
  return `${host}/${IMGPROXY_PATH_PREFIX}/${option}/${toBase64Url(normaliseImgproxySource(path))}`;
}

/** Convenience wrapper for the canonical SEO variant. */
export function buildMasterImageUrl(path: string | undefined | null, publicUrl: string | undefined | null): string {
  return buildImgproxyUrl(path, MASTER_IMGPROXY_OPTION, publicUrl);
}
